import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { MAINTENANCE_ROUTE, MaintenanceInfo } from './maintenance.model';
import { environment } from '../../environments';

const FLAG_URL = '/maintenance.json';
const BYPASS_PARAM = 'maintenanceBypass';
const BYPASS_STORAGE_KEY = 'maintenanceBypass';
const ACTIVE_POLL_MS = 30_000;
const IDLE_POLL_MS = 120_000;
const RECHECK_THROTTLE_MS = 10_000;
const INIT_TIMEOUT_MS = 3_000;

// Uses fetch instead of HttpClient on purpose: the maintenance interceptor
// injects this service, so HttpClient here would be a circular dependency,
// and these checks must not go through the error toasts anyway.
@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  readonly #router = inject(Router);

  readonly #flag = signal<MaintenanceInfo | null>(null);
  readonly #backend = signal<MaintenanceInfo | null>(null);

  readonly info = computed(() => this.#flag() ?? this.#backend());
  readonly active = computed(() => this.info() !== null);

  #returnUrl = '/';
  #wasActive = false;
  #started = false;
  #lastRecheck = 0;
  #timer?: ReturnType<typeof setTimeout>;

  async init(): Promise<void> {
    this.#captureBypassToken();
    await Promise.race([
      this.checkFlag(),
      new Promise(resolve => setTimeout(resolve, INIT_TIMEOUT_MS)),
    ]);
    this.#started = true;
    this.#schedule();
  }

  async checkFlag(): Promise<void> {
    this.#flag.set(await this.#fetchFlag());
    this.#sync();
  }

  /** Re-checks both sources; `manual` clears backend state when there is no status endpoint to ask. */
  async refresh(manual = false): Promise<void> {
    const [flag, backendHealthy] = await Promise.all([
      this.#fetchFlag(),
      this.#isBackendHealthy(manual),
    ]);
    this.#flag.set(flag);
    if (backendHealthy) {
      this.#backend.set(null);
    }
    this.#sync();
  }

  reportBackendMaintenance(message?: string): void {
    if (this.#backend()) {
      return;
    }
    this.#backend.set({ enabled: true, message });
    this.#sync();
  }

  /** Backend unreachable or plain 503 - only re-check the flag, never enter maintenance by itself. */
  reportBackendFailure(): void {
    const now = Date.now();
    if (now - this.#lastRecheck < RECHECK_THROTTLE_MS) {
      return;
    }
    this.#lastRecheck = now;
    void this.checkFlag();
  }

  setReturnUrl(url: string): void {
    this.#returnUrl = url;
  }

  async #fetchFlag(): Promise<MaintenanceInfo | null> {
    try {
      const response = await fetch(`${FLAG_URL}?t=${Date.now()}`, {
        cache: 'no-store',
      });
      if (!response.ok) {
        return null;
      }
      // CloudFront may answer a missing file with index.html - json() throws then.
      const info = (await response.json()) as Partial<MaintenanceInfo> | null;
      if (info?.enabled !== true) {
        return null;
      }
      if (
        info.bypassToken &&
        sessionStorage.getItem(BYPASS_STORAGE_KEY) === info.bypassToken
      ) {
        return null;
      }
      return info as MaintenanceInfo;
    } catch {
      return null;
    }
  }

  async #isBackendHealthy(manual: boolean): Promise<boolean> {
    if (!this.#backend()) {
      return true;
    }
    const statusApi = environment.shared.statusApi;
    if (!statusApi) {
      return manual;
    }
    try {
      const response = await fetch(statusApi, { cache: 'no-store' });
      return response.ok;
    } catch {
      return false;
    }
  }

  #captureBypassToken(): void {
    const token = new URLSearchParams(window.location.search).get(BYPASS_PARAM);
    if (token) {
      sessionStorage.setItem(BYPASS_STORAGE_KEY, token);
    }
  }

  #sync(): void {
    const active = this.active();
    if (active !== this.#wasActive) {
      this.#wasActive = active;
      // Before the initial navigation the route guards take care of redirects.
      if (this.#router.navigated) {
        if (active) {
          this.#returnUrl = this.#router.url;
          void this.#router.navigate(['/', MAINTENANCE_ROUTE]);
        } else {
          void this.#router.navigateByUrl(this.#returnUrl);
        }
      }
    }
    this.#schedule();
  }

  #schedule(): void {
    if (!this.#started) {
      return;
    }
    clearTimeout(this.#timer);
    this.#timer = setTimeout(
      () => void this.refresh(),
      this.active() ? ACTIVE_POLL_MS : IDLE_POLL_MS
    );
  }
}
